import React, { useMemo, useState } from 'react';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { baseGetRowClassName, baseGridSx } from '../../components/Table/gridStyles';
import { usePermissions } from '../../hooks';
import {
  useFetchEmailTemplatesQuery,
  useFetchPushTemplatesQuery,
} from '../../redux/features/notificationTemplate/notificationTemplate.apiSlice';
import { EmailNotificationTemplate, PushNotificationTemplate, TemplateChannel } from '../../types/notificationTemplate';
import { formatDateTime } from '../../utils/date';
import * as permissions from '../../utils/permissions';
import { EmailTemplateDialog } from './EmailTemplateDialog';
import { NotificationTemplateCreateDialog } from './NotificationTemplateCreateDialog';
import { NotificationTemplateDialog } from './NotificationTemplateDialog';

type Template = PushNotificationTemplate | EmailNotificationTemplate;

const renderCopy = (dir: 'rtl' | 'ltr') =>
  function CopyCell({ value }: GridRenderCellParams<PushNotificationTemplate, string>) {
    return (
      <Box dir={dir} sx={{ whiteSpace: 'pre-wrap', py: 1, lineHeight: 1.4 }}>
        {value}
      </Box>
    );
  };

function QuickFilterToolbar() {
  return (
    <Box sx={{ p: 1 }}>
      <GridToolbarQuickFilter />
    </Box>
  );
}

const PAGES: Record<TemplateChannel, { title: string; description: string }> = {
  PUSH: {
    title: 'Push Notifications',
    description:
      'The wording of each push notification, by the key the code sends. When it is sent is set in that code. ' +
      'A key with no active template sends nothing.',
  },
  EMAIL: {
    title: 'Email Notifications',
    description:
      'Which SendGrid template each email key sends; the email itself is edited in SendGrid. A key with no active ' +
      'template sends nothing. Emails not listed here are still sent the old way.',
  },
};

const sharedColumns = <T extends Template>(): GridColDef<T>[] => [
  {
    field: 'key',
    headerName: 'Key',
    width: 260,
    renderCell: ({ value }) => <code>{value}</code>,
  },
  {
    field: 'isActive',
    headerName: 'Status',
    type: 'boolean',
    width: 100,
    renderCell: ({ value }) => (
      <Chip size="small" color={value ? 'success' : 'default'} label={value ? 'Active' : 'Inactive'} />
    ),
  },
];

const updatedColumn = <T extends Template>(): GridColDef<T> => ({
  field: 'updatedAt',
  headerName: 'Updated',
  width: 180,
  valueFormatter: ({ value }) => (value ? formatDateTime(value) : ''),
});

const PUSH_COLUMNS: GridColDef<PushNotificationTemplate>[] = [
  ...sharedColumns<PushNotificationTemplate>(),
  {
    field: 'bodyEn',
    headerName: 'English',
    flex: 1,
    minWidth: 260,
    valueGetter: ({ row }) => [row.titleEn, row.bodyEn].filter(Boolean).join('\n'),
    renderCell: renderCopy('ltr'),
  },
  {
    field: 'bodyAr',
    headerName: 'Arabic',
    flex: 1,
    minWidth: 260,
    valueGetter: ({ row }) => [row.titleAr, row.bodyAr].filter(Boolean).join('\n'),
    renderCell: renderCopy('rtl'),
  },
  updatedColumn<PushNotificationTemplate>(),
];

const EMAIL_COLUMNS: GridColDef<EmailNotificationTemplate>[] = [
  ...sharedColumns<EmailNotificationTemplate>(),
  {
    field: 'sendgridTemplateId',
    headerName: 'SendGrid template',
    width: 320,
    renderCell: ({ value }) => <code>{value}</code>,
  },
  { field: 'description', headerName: 'Description', flex: 1, minWidth: 240 },
  updatedColumn<EmailNotificationTemplate>(),
];

interface NotificationTemplateListProps {
  channel: TemplateChannel;
}

/** Notification templates for one channel (MAD-5123, MAD-4965): content only, one row per key. */
export function NotificationTemplateList({ channel }: NotificationTemplateListProps) {
  const isPush = channel === 'PUSH';
  const push = useFetchPushTemplatesQuery(undefined, { skip: !isPush });
  const email = useFetchEmailTemplatesQuery(undefined, { skip: isPush });
  const { isFetching, isError } = isPush ? push : email;
  const canUpdate = usePermissions({ items: [permissions.NotificationTemplate.update] });
  const canCreate = usePermissions({ items: [permissions.NotificationTemplate.create] });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const pushTemplates = push.data || [];
  const emailTemplates = email.data || [];
  const rows: Template[] = isPush ? pushTemplates : emailTemplates;

  const columns = useMemo<GridColDef<Template>[]>(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 80,
        getActions: ({ row }: GridRowParams<Template>) => [
          <GridActionsCellItem
            key={`notification-template-${row.id}`}
            icon={canUpdate ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />}
            label={canUpdate ? 'Edit' : 'View'}
            onClick={() => setSelectedId(row.id)}
          />,
        ],
      },
      ...((isPush ? PUSH_COLUMNS : EMAIL_COLUMNS) as GridColDef<Template>[]),
    ],
    [canUpdate, isPush],
  );

  return (
    <Container component="main" maxWidth="xl">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ pb: 3 }}>
        <Box>
          <Typography variant="h4">{PAGES[channel].title}</Typography>
          <Typography color="text.secondary">{PAGES[channel].description}</Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} sx={{ flexShrink: 0 }}>
            New template
          </Button>
        )}
      </Stack>
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the templates.
        </Alert>
      )}
      <DataGridPremium
        autoHeight
        rows={rows}
        columns={columns}
        loading={isFetching}
        getRowHeight={() => 'auto'}
        getRowClassName={baseGetRowClassName}
        sx={baseGridSx}
        slots={{ toolbar: QuickFilterToolbar }}
        initialState={{ sorting: { sortModel: [{ field: 'key', sort: 'asc' }] } }}
        disableRowSelectionOnClick
        hideFooter
      />
      {isPush ? (
        <>
          <NotificationTemplateDialog
            template={pushTemplates.find(({ id }) => id === selectedId) || null}
            templates={pushTemplates}
            canUpdate={canUpdate}
            onClose={() => setSelectedId(null)}
          />
          <NotificationTemplateCreateDialog
            open={creating}
            templates={pushTemplates}
            onClose={() => setCreating(false)}
          />
        </>
      ) : (
        <EmailTemplateDialog
          open={creating || selectedId !== null}
          template={creating ? null : emailTemplates.find(({ id }) => id === selectedId) || null}
          templates={emailTemplates}
          canUpdate={canUpdate}
          onClose={() => {
            setCreating(false);
            setSelectedId(null);
          }}
        />
      )}
    </Container>
  );
}
